import React, { useState, useEffect } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Ghost Data Cleanup Dashboard
 * ERP Phase 4.5 - Safe cleanup of test/development data
 * 
 * Features:
 * - Preview-only tables (read-only)
 * - Explicit confirmation required for deletion
 * - Audit logging of all deletions
 */
const GhostDataCleanup = () => {
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);
  const [activeTab, setActiveTab] = useState('users');
  const [previewData, setPreviewData] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [deleteType, setDeleteType] = useState(null);
  const [deleteResult, setDeleteResult] = useState(null);

  useEffect(() => {
    fetchSummary();
  }, []);

  useEffect(() => {
    fetchPreview(activeTab);
  }, [activeTab]);

  const fetchSummary = async () => {
    try {
      const response = await axios.get(`${API}/admin/ghost-data/summary`);
      setSummary(response.data);
    } catch (error) {
      console.error('Error fetching ghost data summary:', error);
    }
  };

  const fetchPreview = async (type) => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/admin/ghost-data/${type}`);
      setPreviewData(response.data);
    } catch (error) {
      console.error(`Error fetching ${type} preview:`, error);
      setPreviewData(null);
    } finally {
      setLoading(false);
    }
  };

  const openConfirmModal = (type) => {
    setDeleteType(type);
    setShowConfirmModal(true);
    setDeleteResult(null);
  };

  const handleDelete = async () => {
    if (!deleteType) return;
    
    setDeleting(true);
    try {
      const response = await axios.delete(`${API}/admin/ghost-data/${deleteType}?confirm=true`);
      setDeleteResult({
        success: true,
        message: response.data.message,
        count: response.data.deleted_count
      });
      // Refresh data
      fetchSummary();
      fetchPreview(activeTab);
    } catch (error) {
      setDeleteResult({
        success: false,
        message: error.response?.data?.detail || 'Deletion failed'
      });
    } finally {
      setDeleting(false);
    }
  };

  const closeConfirmModal = () => {
    setShowConfirmModal(false);
    setDeleteType(null);
    setDeleteResult(null);
  };

  const getTabCount = (type) => {
    if (!summary?.ghost_data_summary) return 0;
    return summary.ghost_data_summary[type]?.safe_to_delete || 0;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: '2-digit' });
  };

  return (
    <div className="space-y-6" data-testid="ghost-data-cleanup">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-xl font-bold text-white">🧹 Ghost Data Cleanup</h2>
          <p className="text-blue-300 text-sm">
            Preview and safely remove test/development data
          </p>
        </div>
        {summary && (
          <div className="text-right">
            <div className="text-2xl font-bold text-yellow-400">
              {summary.total_safe_to_delete}
            </div>
            <div className="text-xs text-blue-300">Items safe to delete</div>
          </div>
        )}
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {Object.entries(summary.ghost_data_summary).map(([key, value]) => (
            <div 
              key={key}
              className={`glass-card p-4 cursor-pointer transition-all ${
                activeTab === key ? 'ring-2 ring-blue-400' : 'hover:bg-blue-500/10'
              }`}
              onClick={() => setActiveTab(key)}
            >
              <div className="text-blue-300 text-xs uppercase mb-1">{key}</div>
              <div className="flex items-baseline gap-2">
                <span className={`text-2xl font-bold ${
                  value.safe_to_delete > 0 ? 'text-yellow-400' : 'text-green-400'
                }`}>
                  {value.safe_to_delete}
                </span>
                <span className="text-blue-400 text-sm">
                  / {value.safe_to_delete + value.protected} total
                </span>
              </div>
              <div className="text-xs text-blue-400 mt-1">
                {value.protected} protected
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Tab Content */}
      <div className="glass-card p-4">
        {/* Tab Buttons */}
        <div className="flex gap-2 mb-4 flex-wrap">
          {['users', 'slots', 'bookings', 'templates'].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition ${
                activeTab === tab
                  ? 'bg-blue-500 text-white'
                  : 'bg-blue-900/30 text-blue-300 hover:bg-blue-900/50'
              }`}
            >
              {tab.charAt(0).toUpperCase() + tab.slice(1)} ({getTabCount(tab)})
            </button>
          ))}
        </div>

        {/* Delete Button */}
        {previewData && getTabCount(activeTab) > 0 && (
          <div className="mb-4 p-3 bg-red-500/10 border border-red-500/30 rounded-lg flex justify-between items-center">
            <div>
              <span className="text-red-300 font-medium">
                {getTabCount(activeTab)} {activeTab} can be safely deleted
              </span>
              <p className="text-red-400/70 text-xs mt-1">
                This action is irreversible. All deletions are logged.
              </p>
            </div>
            <button
              onClick={() => openConfirmModal(activeTab)}
              className="px-4 py-2 bg-red-500/30 text-red-200 rounded-lg hover:bg-red-500/50 transition font-medium"
              data-testid={`delete-${activeTab}-btn`}
            >
              🗑️ Delete {activeTab}
            </button>
          </div>
        )}

        {/* Loading State */}
        {loading && (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-400 mx-auto mb-2"></div>
            <p className="text-blue-300">Loading preview...</p>
          </div>
        )}

        {/* Preview Table */}
        {!loading && previewData && (
          <div className="overflow-x-auto">
            {activeTab === 'users' && (
              <UsersTable data={previewData} formatDate={formatDate} />
            )}
            {activeTab === 'slots' && (
              <SlotsTable data={previewData} />
            )}
            {activeTab === 'bookings' && (
              <BookingsTable data={previewData} />
            )}
            {activeTab === 'templates' && (
              <TemplatesTable data={previewData} formatDate={formatDate} />
            )}
          </div>
        )}
      </div>

      {/* Confirmation Modal */}
      {showConfirmModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center p-4 z-50">
          <div className="glass-card max-w-md w-full p-6 max-h-[90dvh] overflow-y-auto">
            <h3 className="text-xl font-bold text-white mb-4">
              ⚠️ Confirm Deletion
            </h3>
            
            {!deleteResult ? (
              <>
                <p className="text-blue-200 mb-4">
                  You are about to permanently delete <strong className="text-yellow-400">
                    {getTabCount(deleteType)} {deleteType}
                  </strong>.
                </p>
                <p className="text-red-300 text-sm mb-6">
                  This action cannot be undone. All deletions will be logged for audit purposes.
                </p>
                <div className="flex gap-3">
                  <button
                    onClick={handleDelete}
                    disabled={deleting}
                    className="flex-1 py-2 bg-red-500/40 text-white rounded-lg hover:bg-red-500/60 font-medium disabled:opacity-50"
                  >
                    {deleting ? '⏳ Deleting...' : '🗑️ Yes, Delete'}
                  </button>
                  <button
                    onClick={closeConfirmModal}
                    disabled={deleting}
                    className="flex-1 py-2 bg-blue-900/40 text-blue-200 rounded-lg hover:bg-blue-900/60"
                  >
                    Cancel
                  </button>
                </div>
              </>
            ) : (
              <>
                <div className={`p-4 rounded-lg mb-4 ${
                  deleteResult.success ? 'bg-green-500/20' : 'bg-red-500/20'
                }`}>
                  <p className={deleteResult.success ? 'text-green-300' : 'text-red-300'}>
                    {deleteResult.message}
                  </p>
                  {deleteResult.count > 0 && (
                    <p className="text-green-400 text-sm mt-1">
                      Deleted {deleteResult.count} items
                    </p>
                  )}
                </div>
                <button
                  onClick={closeConfirmModal}
                  className="w-full py-2 bg-blue-500/40 text-white rounded-lg hover:bg-blue-500/60"
                >
                  Close
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// Sub-components for tables
const UsersTable = ({ data, formatDate }) => {
  const ghostUsers = data?.ghost_users || [];
  const protectedUsers = data?.protected_users || [];
  
  return (
    <div className="space-y-4">
      {ghostUsers.length > 0 && (
        <>
          <h4 className="text-yellow-400 font-medium">⚠️ Safe to Delete ({ghostUsers.length})</h4>
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-blue-400/30">
                <th className="text-left py-2 text-blue-300">Name</th>
                <th className="text-left py-2 text-blue-300">Email</th>
                <th className="text-left py-2 text-blue-300">Reason</th>
                <th className="text-center py-2 text-blue-300">Bookings</th>
                <th className="text-center py-2 text-blue-300">Workouts</th>
              </tr>
            </thead>
            <tbody>
              {ghostUsers.map((user) => (
                <tr key={user.id} className="border-b border-blue-400/10">
                  <td className="py-2 text-white">{user.full_name}</td>
                  <td className="py-2 text-blue-200">{user.email}</td>
                  <td className="py-2">
                    <span className="text-xs px-2 py-0.5 bg-yellow-500/20 text-yellow-300 rounded">
                      {user.reason_flagged}
                    </span>
                  </td>
                  <td className="py-2 text-center text-blue-300">{user.completed_bookings}</td>
                  <td className="py-2 text-center text-blue-300">{user.finished_workouts}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
      
      {protectedUsers.length > 0 && (
        <>
          <h4 className="text-green-400 font-medium mt-6">✓ Protected (Has Real Activity)</h4>
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-blue-400/30">
                <th className="text-left py-2 text-blue-300">Name</th>
                <th className="text-left py-2 text-blue-300">Email</th>
                <th className="text-center py-2 text-blue-300">Bookings</th>
                <th className="text-center py-2 text-blue-300">Workouts</th>
                <th className="text-center py-2 text-blue-300">Payments</th>
              </tr>
            </thead>
            <tbody>
              {protectedUsers.map((user) => (
                <tr key={user.id} className="border-b border-blue-400/10">
                  <td className="py-2 text-white">{user.full_name}</td>
                  <td className="py-2 text-blue-200">{user.email}</td>
                  <td className="py-2 text-center text-green-300">{user.completed_bookings}</td>
                  <td className="py-2 text-center text-green-300">{user.finished_workouts}</td>
                  <td className="py-2 text-center text-green-300">{user.paid_payments}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
      
      {ghostUsers.length === 0 && protectedUsers.length === 0 && (
        <p className="text-center text-green-400 py-8">✓ No ghost users found</p>
      )}
    </div>
  );
};

const SlotsTable = ({ data }) => {
  const ghostSlots = data?.ghost_slots || [];
  
  return (
    <div>
      {ghostSlots.length > 0 ? (
        <>
          <h4 className="text-yellow-400 font-medium mb-2">⚠️ Empty Past Slots ({ghostSlots.length})</h4>
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-blue-400/30">
                <th className="text-left py-2 text-blue-300">Date</th>
                <th className="text-left py-2 text-blue-300">Time</th>
                <th className="text-center py-2 text-blue-300">Max Clients</th>
                <th className="text-center py-2 text-blue-300">Bookings</th>
              </tr>
            </thead>
            <tbody>
              {ghostSlots.slice(0, 20).map((slot) => (
                <tr key={slot.id} className="border-b border-blue-400/10">
                  <td className="py-2 text-white">{slot.date}</td>
                  <td className="py-2 text-blue-200">{slot.start_time} - {slot.end_time}</td>
                  <td className="py-2 text-center text-blue-300">{slot.max_clients}</td>
                  <td className="py-2 text-center text-blue-300">{slot.bookings_count}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {ghostSlots.length > 20 && (
            <p className="text-blue-400 text-sm mt-2">+ {ghostSlots.length - 20} more</p>
          )}
        </>
      ) : (
        <p className="text-center text-green-400 py-8">✓ No ghost slots found</p>
      )}
    </div>
  );
};

const BookingsTable = ({ data }) => {
  const ghostBookings = data?.ghost_bookings || [];
  
  return (
    <div>
      {ghostBookings.length > 0 ? (
        <>
          <h4 className="text-yellow-400 font-medium mb-2">⚠️ Orphaned Bookings ({ghostBookings.length})</h4>
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-blue-400/30">
                <th className="text-left py-2 text-blue-300">Client</th>
                <th className="text-left py-2 text-blue-300">Date</th>
                <th className="text-center py-2 text-blue-300">Status</th>
                <th className="text-left py-2 text-blue-300">Reason</th>
              </tr>
            </thead>
            <tbody>
              {ghostBookings.map((booking) => (
                <tr key={booking.id} className="border-b border-blue-400/10">
                  <td className="py-2 text-white">{booking.client_name || 'Unknown'}</td>
                  <td className="py-2 text-blue-200">{booking.date}</td>
                  <td className="py-2 text-center">
                    <span className="text-xs px-2 py-0.5 bg-blue-500/20 text-blue-300 rounded">
                      {booking.status}
                    </span>
                  </td>
                  <td className="py-2">
                    <span className="text-xs text-yellow-300">{booking.reason_flagged}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      ) : (
        <p className="text-center text-green-400 py-8">✓ No ghost bookings found</p>
      )}
    </div>
  );
};

const TemplatesTable = ({ data, formatDate }) => {
  const ghostTemplates = data?.ghost_templates || [];
  
  return (
    <div>
      {ghostTemplates.length > 0 ? (
        <>
          <h4 className="text-yellow-400 font-medium mb-2">⚠️ Unused Templates ({ghostTemplates.length})</h4>
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-blue-400/30">
                <th className="text-left py-2 text-blue-300">Name</th>
                <th className="text-center py-2 text-blue-300">Exercises</th>
                <th className="text-center py-2 text-blue-300">Usage</th>
                <th className="text-left py-2 text-blue-300">Reason</th>
              </tr>
            </thead>
            <tbody>
              {ghostTemplates.map((template) => (
                <tr key={template.id} className="border-b border-blue-400/10">
                  <td className="py-2 text-white">{template.name || 'Unnamed'}</td>
                  <td className="py-2 text-center text-blue-300">{template.exercises_count}</td>
                  <td className="py-2 text-center text-blue-300">{template.usage_count}</td>
                  <td className="py-2">
                    <span className="text-xs text-yellow-300">{template.reason_flagged}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      ) : (
        <p className="text-center text-green-400 py-8">✓ No ghost templates found</p>
      )}
    </div>
  );
};

export default GhostDataCleanup;
